/* eslint-disable no-unused-vars */
import React, { useEffect, useCallback, useState } from 'react';
import { format } from 'date-fns';
import { Button, Modal, Space, Tag, Tooltip } from 'antd';
import useListFilters from '../../hooks/useListFilters';
import ListPage from '../../components/core/ListPage';
import { APP_TERMS } from '../../app-terms';
import { AvailableAppLocales } from '../../availableAppLocales';
import { useUserLocale } from '../../hooks/useLocale';
import {
  getInvoiceEmailSentStatusTagColor,
  getInvoicePaymentStatusTagColorWithPaymentPlatform,
  getInvoiceTypeText,
} from '../../utils/clinic';
import { useNavigate } from 'react-router-dom';
import { getMonthTitleFromMonthNumber } from '../../utils/date';
import { useInvoiceList } from '../../hooks/invoice/useInvoiceList';
import InvoicePreviewDownloader from '../../components/invoices/InvoicePreviewDownloader';
import SelectInvoiceDurationForm from '../../components/invoices/SelectInvoiceDurationForm';
import { useCreateMonthlyCaseInvoices } from '../../hooks/invoice/useCreateMonthlyCaseInvoices';
import InvoiceEmailSender from '../../components/invoices/InvoiceEmailSender';
import MarkInvoiceAsPaid from '../../components/invoices/MarkInvoiceAsPaid';
const BREAD_CRUMBS = [
  { label: 'Invoice', to: '/user/invoicelist' },
  { label: 'Invoice List', to: '/user/invoicelist' },
];

export const InvoiceList = () => {
  const { data, getList } = useInvoiceList();
  const {
    search,
    setSearch,
    pagination,
    setPagination,
    sortParams,
    setSortParams,
    loading,
    setLoading,
  } = useListFilters();
  const [modal, contextHolder] = Modal.useModal();
  const [open, setOpen] = useState(false);
  const { userLocale } = useUserLocale();
  const { loading: creatingInvoices, createMonthlyCaseInvoices } =
    useCreateMonthlyCaseInvoices();
  const navigate = useNavigate();

  // Table
  const handleEdit = (record) => {
    navigate(`/user/invoice/edit?id=${record?.invoiceId}`);
  };
  // const handleGenerateMontlyInvoices = () => setOpen(true);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getList({
        search,
        page: pagination.current,
        limit: pagination.pageSize,
        sortOrder: sortParams.sortOrder,
        sortBy: sortParams.sortField,
      });
      setLoading(false);
      setPagination({ total: response.totalItems });
    } catch (error) {
      setLoading(false);
    }
  }, [
    pagination.current,
    pagination.pageSize,
    sortParams.sortOrder,
    sortParams.sortField,
    search,
  ]);
  const handleEmailSent = () => reload();

  // Modal
  const handleCancel = () => setOpen(false);
  const handleSubmit = async (values) => {
    try {
      handleCancel();
      if (!values) return;
      await createMonthlyCaseInvoices(values);
      reload();
    } catch (error) {
      console.log('Error on create monthly case invoices');
    }
  };

  const handleTableChange = (newPagination, _, sorter) => {
    const pageSizeChanged = newPagination?.pageSize !== pagination.pageSize;
    const newPage = pageSizeChanged ? 1 : newPagination?.current || 1;
    setSortParams({
      sortOrder: Array.isArray(sorter) ? undefined : sorter.order,
      sortField: Array.isArray(sorter) ? undefined : sorter.field,
    });
    setPagination({ current: newPage, pageSize: newPagination?.pageSize });
  };

  const handleSearch = (searchValue) => {
    setSearch(searchValue);
  };

  useEffect(() => {
    reload();
  }, [
    pagination.current,
    pagination.pageSize,
    sortParams.sortOrder,
    sortParams.sortField,
    search,
  ]);

  const columns = [
    {
      title: 'Invoice No.',
      dataIndex: 'invoiceNo',
      sorter: true,
      render: (text) => {
        if (!text) return text;
        const value = text.toString();
        const parts = value.split('-');
        return parts.length > 1 ? parts.slice(1).join('-') : value;
      },
    },
    {
      title: 'Invoice Type',
      dataIndex: 'invoiceType',
      sorter: true,
      render: (_, { invoiceType }) => {
        return invoiceType ? getInvoiceTypeText(invoiceType) : undefined;
      },
    },
    {
      title: 'Clinic Location Title',
      dataIndex: 'clinicLocationTitle',
      sorter: true,
      ellipsis: {
        showTitle: false,
      },
      render: (text) => (
        <Tooltip placement="topLeft" title={text}>
          {text}
        </Tooltip>
      ),
    },
    {
      title: 'Month-Year',
      dataIndex: 'monthNumber',
      sorter: true,
      render: (_, { monthNumber, yearNumber }) => {
        if (monthNumber < 0 || !yearNumber) {
          return undefined;
        }
        const date = new Date();
        date.setMonth(monthNumber);
        const monthTitle = date.toLocaleString('en-US', { month: 'short' });
        return `${monthTitle}-${yearNumber}`;
      },
    },
    {
      title: 'Payment Status',
      dataIndex: 'paymentId',
      render: (_, record, index) => {
        const services = record?.invoiceCaseServices || [];

        let color = 'red';
        let text = 'UN-PAID';

        if (services.length > 0) {
          const allPaid = services.every(s => s.paymentStatus === true);

          if (allPaid) {
            color = 'green';
            text = record?.paymentPlatform
              ? `Paid with ${record.paymentPlatform}`
              : 'Paid with CHEQUE';
          }
        }

        return (
          <Tag color={color} key={index}>
            {text}
          </Tag>
        );
      },
    },
    {
      title: 'Send Status',
      dataIndex: 'emailedStatus',
      render: (_, { emailedStatus }, index) => {
        let statusMeta = getInvoiceEmailSentStatusTagColor(emailedStatus);
        return (
          <Tag color={statusMeta?.color} key={index}>
            {statusMeta?.text}
          </Tag>
        );
      },
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      render: (text) =>
        format(new Date(text), APP_TERMS.date_1_format, {
          locale: AvailableAppLocales?.[userLocale],
        }),
      sorter: true,
    },
    {
      title: 'Actions',
      dataIndex: '',
      render: (_, record) => {
        const services = record?.invoiceCaseServices || [];
        // UNPAID if ANY service paymentStatus === false
        const isUnpaid =
          services.length === 0 || services.some(s => s.paymentStatus === false);
        return (
          <Space size="middle">
            {isUnpaid && (
              <Tooltip title="Edit Invoice" placement="left">
                <Button
                  icon={<i className="fa fa-edit" />}
                  onClick={() => handleEdit(record)}
                />
              </Tooltip>
            )}
            <InvoicePreviewDownloader
              invoiceId={record?.invoiceId}
              invoiceNo={record?.invoiceNo}
              isShowText={false}
            />
            <InvoiceEmailSender
              invoiceId={record?.invoiceId}
              onEmailSent={handleEmailSent}
              clinicLocationId={record?.clinicLocationId}
            />
            {/* SHOW ONLY IF UNPAID */}
            {isUnpaid && (
              <MarkInvoiceAsPaid
                invoiceId={record?.invoiceId}
                onMarkInvoiceAsPaid={reload}
                placement="left"
                isUnpaid={isUnpaid}
              />
            )}
            {contextHolder}
          </Space>
        );
      },
    },
  ];

  return (
    <>
      <ListPage
        title={'Invoice List'}
        data={data?.items || []}
        columns={columns}
        loading={loading}
        pagination={pagination}
        rowKey={(record) => record?.invoiceId}
        onRefresh={reload}
        onSearch={handleSearch}
        onTableChange={handleTableChange}
        breadCrumbItems={BREAD_CRUMBS}
        hideAddAction
        showCheckbox={false}
      />
      <Modal
        title={'Select Invoice Duration'}
        open={open}
        onCancel={handleCancel}
        maskClosable={false}
        footer={null}
        width={300}
      >
        <SelectInvoiceDurationForm
          onCancel={handleCancel}
          onSubmit={handleSubmit}
        />
      </Modal>
    </>
  );
};
